import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signOut, onAuthStateChanged, sendPasswordResetEmail as firebaseSendPasswordResetEmail, User, Auth } from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firebaseInitialized = false;

// Try to initialize Firebase, but don't break if it fails
try {
  if (firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    firebaseInitialized = true;
  } else {
    console.warn('Firebase config missing. Authentication features will be unavailable.');
  }
} catch (error) {
  console.warn('Firebase initialization failed. Authentication features will be unavailable.', error);
}

export { auth, firebaseInitialized };

export const signInWithEmail = async (email: string, password: string) => {
  if (!auth) {
    throw new Error('Firebase is not configured. Please set up Firebase credentials.');
  }
  const result = await signInWithEmailAndPassword(auth, email, password);
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email || '',
    displayName: result.user.displayName || '',
  };
};

export const signUpWithEmail = async (email: string, password: string, displayName: string) => {
  if (!auth) {
    throw new Error('Firebase is not configured. Please set up Firebase credentials.');
  }
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(result.user, { displayName });
  const idToken = await result.user.getIdToken();
  return {
    idToken,
    email: result.user.email || '',
    displayName,
  };
};

export const firebaseSignOut = () => {
  if (!auth) {
    return Promise.resolve();
  }
  return signOut(auth);
};

export const sendPasswordResetEmail = (email: string) => {
  if (!auth) {
    throw new Error('Firebase is not configured. Please set up Firebase credentials.');
  }
  return firebaseSendPasswordResetEmail(auth, email);
};

export const onFirebaseAuthStateChanged = (callback: (user: User | null) => void) => {
  if (!auth) {
    // Call with null immediately and return a no-op unsubscribe
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};
